import React, { useRef, useMemo, useCallback, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Send } from 'lucide-react';
import { prepareTaskDescriptionHtmlForSave } from '../../../utils/task_description_html';
import { compressImageForUpload } from '../../../utils/compress_image';
import { upload_card_comment_image } from '../../../utils/talent_services';
import './StrategyCommentComposer.css';

const COMMENT_COLORS = ['#1f2937', '#dc2626', '#1e3a5f', '#14532d', '#6366f1'];

const COMMENT_FORMATS = [
    'bold',
    'italic',
    'underline',
    'list',
    'link',
    'color',
    'image',
];

function imageUploadErrorMessage(err) {
    const code = typeof err === 'string' ? err : err?.message;
    if (code === 'invalid_type') return 'Solo se permiten imágenes (JPEG, PNG, WebP o GIF).';
    if (code === 'too_large') return 'La imagen es demasiado pesada. Probá con una más liviana.';
    if (code === 'denied' || code === 'not_found') return 'No tenés permiso para subir imágenes en este comentario.';
    return 'No se pudo subir la imagen. Intentá de nuevo.';
}

/**
 * Compositor compacto (ReactQuill) para comentarios de estrategia.
 */
const StrategyCommentComposer = ({
    value,
    onChange,
    onSubmit,
    disabled = false,
    placeholder = 'Escribe un comentario…',
    editorKey = 'comment',
    trelloCardId = null,
    token = null,
    uploadImageFn = null,
}) => {
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const onSubmitRef = useRef(onSubmit);
    const uploadImageRef = useRef(null);
    const uploadingLockRef = useRef(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    onChangeRef.current = onChange;
    onSubmitRef.current = onSubmit;

    const getEditorHtml = useCallback(() => {
        const quill = quillRef.current?.getEditor?.();
        return quill?.root?.innerHTML ?? '';
    }, []);

    const uploadAndInsertImage = useCallback(
        async (file) => {
            const canUpload = uploadImageFn
                ? Boolean(token)
                : Boolean(file && trelloCardId && token);
            if (!file || !canUpload) {
                window.alert('No se puede subir la imagen en este momento.');
                return;
            }
            if (uploadingLockRef.current) return;

            uploadingLockRef.current = true;
            setUploadingImage(true);
            try {
                const compressed = await compressImageForUpload(file);
                const result = uploadImageFn
                    ? await uploadImageFn(compressed)
                    : await upload_card_comment_image(token, trelloCardId, compressed);
                if (!result || result === 'INVALID' || result.ok !== true || !result.url) {
                    throw new Error(result?.message || 'upload_failed');
                }

                const quill = quillRef.current?.getEditor?.();
                if (!quill) return;
                quill.enable(true);
                const range = quill.getSelection(true);
                const index = range ? range.index : Math.max(0, quill.getLength() - 1);
                quill.insertEmbed(index, 'image', result.url, 'user');
                quill.insertText(index + 1, '\n', 'user');
                quill.setSelection(index + 2, 0, 'silent');
                onChangeRef.current?.(quill.root.innerHTML);
            } catch (err) {
                window.alert(imageUploadErrorMessage(err));
            } finally {
                uploadingLockRef.current = false;
                setUploadingImage(false);
            }
        },
        [trelloCardId, token, uploadImageFn]
    );

    uploadImageRef.current = uploadAndInsertImage;

    const imageHandler = useCallback(() => {
        const canUpload = uploadImageFn ? Boolean(token) : Boolean(trelloCardId && token);
        if (!canUpload) {
            window.alert('No se puede subir la imagen en este momento.');
            return;
        }
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/jpeg,image/png,image/webp,image/gif');
        input.click();
        input.onchange = () => {
            const file = input.files?.[0];
            if (file) uploadImageRef.current?.(file);
        };
    }, [trelloCardId, token, uploadImageFn]);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link', 'image'],
                    [{ color: COMMENT_COLORS }],
                    ['clean'],
                ],
                handlers: {
                    image: imageHandler,
                },
            },
            clipboard: {
                matchVisual: false,
            },
        }),
        [imageHandler]
    );

    useEffect(() => {
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;

        const collectImageFiles = (dataTransfer) => {
            if (!dataTransfer) return [];
            const fromFiles = [...(dataTransfer.files || [])].filter((f) =>
                (f.type || '').startsWith('image/')
            );
            if (fromFiles.length > 0) return fromFiles;
            const items = dataTransfer.items ? [...dataTransfer.items] : [];
            const fromItems = [];
            for (const item of items) {
                if (item.kind === 'file' && (item.type || '').startsWith('image/')) {
                    const file = item.getAsFile();
                    if (file) fromItems.push(file);
                }
            }
            return fromItems;
        };

        const onPaste = (e) => {
            const imageFiles = collectImageFiles(e.clipboardData);
            if (imageFiles.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                (async () => {
                    for (const file of imageFiles) {
                        await uploadImageRef.current?.(file);
                    }
                })();
                return;
            }
            window.setTimeout(() => {
                const cleaned = prepareTaskDescriptionHtmlForSave(quill.root.innerHTML);
                if (cleaned !== quill.root.innerHTML) {
                    quill.root.innerHTML = cleaned;
                }
                onChangeRef.current?.(quill.root.innerHTML);
            }, 0);
        };

        const onDrop = (e) => {
            const imageFiles = collectImageFiles(e.dataTransfer);
            if (imageFiles.length === 0) return;
            e.preventDefault();
            e.stopPropagation();
            (async () => {
                for (const file of imageFiles) {
                    await uploadImageRef.current?.(file);
                }
            })();
        };

        const onDragOver = (e) => {
            if (collectImageFiles(e.dataTransfer).length > 0) {
                e.preventDefault();
            }
        };

        const onKeyDown = (e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                onSubmitRef.current?.(getEditorHtml());
            }
        };

        quill.root.addEventListener('paste', onPaste, true);
        quill.root.addEventListener('drop', onDrop, true);
        quill.root.addEventListener('dragover', onDragOver, true);
        quill.root.addEventListener('keydown', onKeyDown);
        return () => {
            quill.root.removeEventListener('paste', onPaste, true);
            quill.root.removeEventListener('drop', onDrop, true);
            quill.root.removeEventListener('dragover', onDragOver, true);
            quill.root.removeEventListener('keydown', onKeyDown);
        };
    }, [editorKey, getEditorHtml]);

    // ReactQuill pasa un editor "unprivileged" (getHTML/getText…), sin `.root`.
    // Usar el `content` (HTML) o editor.getHTML(); si se exige `.root`, onChange no corre y el botón queda siempre disabled.
    const handleQuillChange = useCallback((content, _delta, _source, editor) => {
        const html =
            typeof content === 'string'
                ? content
                : typeof editor?.getHTML === 'function'
                  ? editor.getHTML()
                  : '';
        onChangeRef.current?.(html);
    }, []);

    const prepared = prepareTaskDescriptionHtmlForSave(value || '');
    const canSubmit = prepared !== '' && !disabled && !uploadingImage;

    return (
        <div
            className={`strategy-comment-composer${uploadingImage ? ' is-uploading-image' : ''}`}
        >
            <div className="strategy-comment-quill">
                <ReactQuill
                    key={editorKey}
                    ref={quillRef}
                    theme="snow"
                    defaultValue={value || ''}
                    onChange={handleQuillChange}
                    modules={modules}
                    formats={COMMENT_FORMATS}
                    placeholder={placeholder}
                />
            </div>
            {uploadingImage && (
                <div className="strategy-comment-upload-status" aria-live="polite">
                    Subiendo imagen…
                </div>
            )}
            <button
                type="button"
                className="strategy-modal-comments-send strategy-comment-composer-send"
                title="Enviar comentario"
                aria-label="Enviar comentario"
                disabled={!canSubmit}
                onClick={() => onSubmitRef.current?.(getEditorHtml())}
            >
                <Send size={18} strokeWidth={2.2} />
            </button>
        </div>
    );
};

export default StrategyCommentComposer;
